/**
 * threadExport — turns a conversation (the `messages` array assembled by
 * `useThread`) into a self-contained, printable transcript and hands it to the
 * browser's print dialog. That single path covers both "drucken" and
 * "als PDF speichern", which is why there is no PDF library involved.
 *
 * Body sources, in order of preference per message:
 *   - our OWN outgoing mail → the inline `content` HTML that actually left the
 *     server (sanitized), so our formatting and footer survive
 *   - anything else → the always-present `bodyText`
 *
 * We deliberately do NOT pull the `htmlStorageUrl` snapshots of inbound mail:
 * they are separate cross-origin documents, and nesting them as iframes prints
 * blank or truncated in Chrome. Text keeps the transcript selectable, offline
 * safe and free of per-message network round-trips.
 *
 * The quoted history is stripped from every message via the same splitter the
 * UI uses — each message already appears as its own entry, so keeping the
 * embedded chains would multiply the length of the export.
 */

import DOMPurify from 'dompurify';
import { splitHtmlQuote, splitTextQuote } from './quoteSplitter';
import { parseAddress } from './emailParsing';

// Matches the cap the forward payload uses — generous enough for rich bodies,
// tight enough that one runaway newsletter can't produce a 200-page document.
const MAX_BODY_CHARS = 50000;

// A printed archive must not read differently depending on who opened it, so
// dates are fixed to the document's language instead of the browser locale
// (which is why this doesn't reuse the UI's `formatFullDate`).
const EXPORT_LOCALE = 'de-DE';

// Give up on a Storage body rather than leaving the user staring at a
// "preparing" tab forever.
const REMOTE_BODY_TIMEOUT_MS = 8000;

// Email HTML is inline-styled, so dropping <style>/<link> costs almost no
// fidelity but stops a mail's global CSS from repainting the whole transcript.
const SANITIZE_CONFIG = {
  FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form', 'input', 'button'],
};

const escapeHtml = (s) =>
  String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const textToHtml = (s) =>
  escapeHtml(String(s).replace(/\r\n?/g, '\n')).replace(/\n/g, '<br>');

const toMillis = (value) => {
  if (value == null) return 0;
  if (typeof value === 'object' && typeof value.toMillis === 'function') return value.toMillis();
  const ms = typeof value === 'number' ? value : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

const formatDateTime = (value) => {
  const ms = toMillis(value);
  if (!ms) return '';
  return new Date(ms).toLocaleString(EXPORT_LOCALE, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Local calendar day for the filename. `toISOString()` would be UTC and could
// name a late-evening export after the previous day.
const todayLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// `isScheduled` / `isSafeGuardDraft` come from the tab decorators in
// useInboxData; a draft or a queued mail is ours even though it never passed
// through world_sent (and therefore never got `isOutgoing` from useThread).
const isOwnOutgoing = (msg) =>
  msg.isOutgoing === true ||
  msg.isSent === true ||
  msg.isForwarded === true ||
  msg.isScheduled === true ||
  msg.isSafeGuardDraft === true;

const formatBytes = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

// "Re: AW: Fwd: Angebot" → "angebot". Used to decide whether a message's own
// subject actually differs from the transcript headline or is just a reply
// prefix stacked on top of it.
const normalizeSubject = (s) =>
  String(s || '')
    .replace(/^(\s*(re|aw|fwd|fw|wg)\s*:\s*)+/i, '')
    .trim()
    .toLowerCase();

const capText = (s) =>
  s.length > MAX_BODY_CHARS ? `${s.slice(0, MAX_BODY_CHARS)}\n\n[…gekürzt]` : s;

const capHtml = (s) =>
  s.length > MAX_BODY_CHARS
    ? `${s.slice(0, MAX_BODY_CHARS)}<p class="tx-trunc">[…gekürzt]</p>`
    : s;

const inlineBodyOf = (msg) => msg.bodyHtml || msg.content || '';
const plainBodyOf = (msg) => msg.bodyText || msg.text || '';

/**
 * True when a message carries no body in its Firestore document at all.
 *
 * The fetcher stores `bodyText = parsedMail.text` without deriving it from
 * HTML, and it never writes `bodyHtml` for inbound mail — so an HTML-only mail
 * (no text/plain part, common for anything designed in a newsletter tool) has
 * its body *exclusively* in Storage. Without fetching that, the export would
 * silently degrade to the 300-character `preview`.
 */
const needsRemoteBody = (msg) =>
  !inlineBodyOf(msg) && !plainBodyOf(msg) && Boolean(msg.htmlStorageUrl);

const sanitizeHtmlBody = (html) =>
  DOMPurify.sanitize(capHtml(html), SANITIZE_CONFIG);

const renderBody = (msg) => {
  const html = inlineBodyOf(msg);
  const text = plainBodyOf(msg);

  // Body recovered from Storage (see hydrateRemoteBodies) wins: for those
  // messages it is the only complete source.
  if (msg._exportHtml) {
    const { mainHtml } = splitHtmlQuote(msg._exportHtml);
    return sanitizeHtmlBody(mainHtml || msg._exportHtml);
  }

  if (isOwnOutgoing(msg) && html) {
    const { mainHtml } = splitHtmlQuote(html);
    return sanitizeHtmlBody(mainHtml || html);
  }

  if (text) {
    const { mainText } = splitTextQuote(text);
    return `<div class="tx-plain">${textToHtml(capText(mainText || text))}</div>`;
  }

  if (html) {
    const { mainHtml } = splitHtmlQuote(html);
    return sanitizeHtmlBody(mainHtml || html);
  }

  // Everything below is a degraded body — say so instead of quietly printing a
  // truncated snippet as if it were the whole message.
  if (msg.preview) {
    return `<div class="tx-plain">${textToHtml(capText(msg.preview))}</div>
        <p class="tx-note">Nur die Vorschau dieser Nachricht war verfügbar — der vollständige Inhalt konnte nicht geladen werden.</p>`;
  }

  return '<p class="tx-empty">(Kein Textinhalt verfügbar)</p>';
};

const renderAttachments = (msg) => {
  const list = (Array.isArray(msg.attachments) ? msg.attachments : []).filter(Boolean);
  if (list.length === 0) return '';

  const items = list
    .map((a) => {
      const size = formatBytes(a.size);
      const name = escapeHtml(a.filename || 'Datei');
      return `<li>${name}${size ? ` <span class="tx-att-size">(${size})</span>` : ''}</li>`;
    })
    .join('');

  return `
      <div class="tx-att">
        <div class="tx-att-label">${list.length} ${list.length === 1 ? 'Anhang' : 'Anhänge'}</div>
        <ul class="tx-att-list">${items}</ul>
      </div>`;
};

/**
 * One message entry. A transcript reads as "who said what", so the headline is
 * always the author (`from`) regardless of direction — the recipient moves to
 * the address line below it.
 *
 * @param {object} msg
 * @param {number} index
 * @param {string} docSubject the transcript's headline subject; a per-message
 *   subject is only shown when it deviates (e.g. the topic changed mid-thread)
 */
const renderMessage = (msg, index, docSubject) => {
  const outgoing = isOwnOutgoing(msg);
  const author = parseAddress(msg.from || (outgoing ? msg.accountEmail : '') || '');
  const recipient = parseAddress(msg.to || msg.recipients || (outgoing ? '' : msg.accountEmail) || '');
  const when = formatDateTime(msg.timestamp || msg.date);

  const name = author.name || author.email || 'Unbekannt';
  const addressLine = [
    author.email ? `<strong>Von:</strong> ${escapeHtml(author.email)}` : '',
    recipient.email || recipient.name
      ? `<strong>An:</strong> ${escapeHtml(recipient.email || recipient.name)}`
      : '',
  ]
    .filter(Boolean)
    .join(' &nbsp;·&nbsp; ');

  const subject = (msg.subject || '').trim();
  const showSubject = Boolean(subject) && normalizeSubject(subject) !== normalizeSubject(docSubject);

  return `
    <article class="tx-msg ${outgoing ? 'out' : 'in'}">
      <header class="tx-msg-head">
        <span class="tx-msg-no">${index + 1}</span>
        <span class="tx-msg-name">${escapeHtml(name)}</span>
        <span class="tx-dir ${outgoing ? 'out' : 'in'}">${outgoing ? 'Gesendet' : 'Empfangen'}</span>
        <span class="tx-msg-date">${escapeHtml(when)}</span>
      </header>
      ${addressLine ? `<div class="tx-msg-addr">${addressLine}</div>` : ''}
      ${showSubject ? `<div class="tx-msg-subject">Betreff: ${escapeHtml(subject)}</div>` : ''}
      <div class="tx-msg-body">${renderBody(msg)}</div>
      ${renderAttachments(msg)}
    </article>`;
};

const PRINT_CSS = `
  @page { margin: 16mm 14mm; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 28px 24px 40px;
    background: #f5f6f8;
    font: 13px/1.55 -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #14181d;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .tx-sheet { max-width: 820px; margin: 0 auto; }
  .tx-toolbar {
    display: flex; justify-content: flex-end; gap: 8px; margin-bottom: 16px;
  }
  .tx-toolbar button {
    font: inherit; font-weight: 600; cursor: pointer;
    padding: 8px 16px; border-radius: 8px;
    border: 1px solid #1f6feb; background: #1f6feb; color: #fff;
  }
  .tx-head {
    background: #fff; border: 1px solid #e3e6ea; border-radius: 10px;
    padding: 18px 20px; margin-bottom: 18px;
  }
  .tx-head h1 { margin: 0 0 4px; font-size: 18px; line-height: 1.3; }
  .tx-head-kicker {
    margin: 0 0 10px; font-size: 11px; font-weight: 700;
    letter-spacing: .08em; text-transform: uppercase; color: #7a8390;
  }
  .tx-head-meta { margin: 0; font-size: 12px; color: #5b636e; }
  .tx-head-meta div { margin-top: 3px; }
  .tx-msg {
    background: #fff; border: 1px solid #e3e6ea; border-left: 3px solid #cfd6de;
    border-radius: 10px; padding: 14px 18px; margin-bottom: 14px;
    page-break-inside: avoid; break-inside: avoid;
  }
  .tx-msg.out { border-left-color: #1f6feb; }
  .tx-msg-head { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
  .tx-msg-no {
    flex: none; width: 20px; height: 20px; border-radius: 50%;
    background: #eef1f5; color: #5b636e;
    font-size: 11px; font-weight: 700; text-align: center; line-height: 20px;
  }
  .tx-msg-name { font-weight: 700; font-size: 14px; }
  .tx-dir {
    font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase;
    padding: 2px 7px; border-radius: 20px; background: #eef1f5; color: #5b636e;
  }
  .tx-dir.out { background: #e7f0ff; color: #1f6feb; }
  .tx-msg-date { margin-left: auto; font-size: 11px; color: #7a8390; white-space: nowrap; }
  .tx-msg-addr { margin-top: 6px; font-size: 11px; color: #5b636e; }
  .tx-msg-addr strong { font-weight: 600; color: #7a8390; }
  .tx-msg-subject { margin-top: 8px; font-size: 12px; font-style: italic; color: #5b636e; }
  .tx-msg-body {
    margin-top: 12px; padding-top: 12px; border-top: 1px solid #eef1f5;
    font-size: 13px; word-break: break-word; overflow-wrap: anywhere;
  }
  .tx-msg-body img { max-width: 100% !important; height: auto !important; }
  /* Declared 1x1 images are tracking pixels — they'd otherwise print as a
     broken-image icon once the sender's endpoint refuses the request. */
  .tx-msg-body img[width="1"], .tx-msg-body img[height="1"] { display: none !important; }
  .tx-msg-body table { max-width: 100% !important; }
  .tx-msg-body a { color: #1f6feb; }
  .tx-plain { white-space: pre-wrap; }
  .tx-empty, .tx-trunc { color: #9aa3ae; font-style: italic; }
  .tx-note {
    margin: 10px 0 0; padding: 6px 10px; border-radius: 6px;
    background: #fff6e5; color: #8a5a00; font-size: 11px;
  }
  .tx-status { text-align: center; color: #7a8390; padding: 60px 20px; }
  .tx-att { margin-top: 12px; padding-top: 10px; border-top: 1px dashed #e3e6ea; }
  .tx-att-label { font-size: 11px; font-weight: 700; color: #7a8390; }
  .tx-att-list { margin: 6px 0 0; padding-left: 18px; font-size: 12px; color: #5b636e; }
  .tx-att-size { color: #9aa3ae; }
  .tx-foot {
    margin-top: 18px; text-align: center; font-size: 10px; color: #9aa3ae;
  }
  @media print {
    body { background: #fff; padding: 0; }
    .tx-toolbar { display: none !important; }
    .tx-head, .tx-msg { border-color: #d8dde3; }
  }
`;

/**
 * Chronological (oldest first) copy of the thread — a transcript reads
 * top-to-bottom, the opposite of the newest-first reading pane.
 */
const orderChronologically = (messages) =>
  [...(Array.isArray(messages) ? messages : [])]
    .filter(Boolean)
    .sort((a, b) => toMillis(a.timestamp || a.date) - toMillis(b.timestamp || b.date));

/**
 * The other party of the conversation — preferred headline for the document
 * title. Falls back through business name → first inbound sender → subject.
 */
const resolveCounterpartName = (messages, selectedEmail) => {
  const business = (
    selectedEmail?.businessName ||
    selectedEmail?.business_name ||
    ''
  ).trim();
  if (business) return business;

  const inbound = messages.find((m) => !isOwnOutgoing(m));
  const source = inbound ? inbound.from : (messages[0]?.to || messages[0]?.recipients);
  const person = parseAddress(source || '');
  return person.name || person.email || 'Unbekannt';
};

/**
 * Builds the export document title. Browsers use it both for the print header
 * and as the default filename when the user picks "Save as PDF".
 */
export const buildExportTitle = (messages, selectedEmail) => {
  const name = resolveCounterpartName(messages, selectedEmail)
    .replace(/[\\/:*?"<>|]+/g, '-')
    .trim();
  return `Chatverlauf – ${name} – ${todayLocal()}`;
};

/**
 * Renders the whole conversation as one standalone HTML document.
 *
 * @param {object[]} messages thread messages (any order)
 * @param {object} [selectedEmail] the mail the user had open (subject/business fallback)
 * @returns {string} complete HTML document
 */
export const buildThreadExportHtml = (messages, selectedEmail) => {
  const ordered = orderChronologically(messages);
  const title = buildExportTitle(ordered, selectedEmail);
  // The oldest message carries the original subject — a nicer headline than the
  // "Re: Re: …" version the user happened to have open.
  const subject = ordered[0]?.subject || selectedEmail?.subject || '(Kein Betreff)';
  const counterpart = resolveCounterpartName(ordered, selectedEmail);

  // A To line can hold several addresses; parseAddress only ever reads one, so
  // split first or everyone but the first recipient disappears from the header.
  const participants = Array.from(
    new Set(
      ordered
        .flatMap((m) => [m.from, m.to, m.recipients])
        .flatMap((v) => (typeof v === 'string' ? v.split(/[,;]+/) : [v]))
        .map((v) => parseAddress(v || '').email)
        .filter(Boolean)
    )
  );

  const first = ordered.length ? formatDateTime(ordered[0].timestamp || ordered[0].date) : '';
  const last = ordered.length
    ? formatDateTime(ordered[ordered.length - 1].timestamp || ordered[ordered.length - 1].date)
    : '';
  const period = first && last && first !== last ? `${first} – ${last}` : (first || last);

  const body = ordered.map((msg, i) => renderMessage(msg, i, subject)).join('');

  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(title)}</title>
<style>${PRINT_CSS}</style>
<script>
  /* Mail images can be long-dead remote URLs; a broken-image icon in a printed
     archive is worse than no image. Capture phase, because image error events
     do not bubble. */
  document.addEventListener('error', function (e) {
    var el = e.target;
    if (el && el.tagName === 'IMG') el.style.display = 'none';
  }, true);
</script>
</head>
<body>
  <div class="tx-sheet">
    <div class="tx-toolbar">
      <button type="button" onclick="window.print()">Drucken / Als PDF speichern</button>
    </div>

    <div class="tx-head">
      <p class="tx-head-kicker">Chatverlauf</p>
      <h1>${escapeHtml(subject)}</h1>
      <div class="tx-head-meta">
        <div><strong>Kontakt:</strong> ${escapeHtml(counterpart)}</div>
        <div><strong>Nachrichten:</strong> ${ordered.length}</div>
        ${period ? `<div><strong>Zeitraum:</strong> ${escapeHtml(period)}</div>` : ''}
        ${participants.length ? `<div><strong>Beteiligte:</strong> ${escapeHtml(participants.join(', '))}</div>` : ''}
        <div><strong>Exportiert:</strong> ${escapeHtml(formatDateTime(Date.now()))}</div>
      </div>
    </div>

    ${body}

    <p class="tx-foot">Exportiert aus BonusClub · Zitierte Verläufe innerhalb einzelner Nachrichten wurden entfernt, da jede Nachricht separat aufgeführt ist.</p>
  </div>
</body>
</html>`;
};

/**
 * Fetches the Storage snapshot for every message whose body isn't in Firestore
 * (see `needsRemoteBody`) and returns copies carrying it as `_exportHtml`.
 *
 * Failures are swallowed on purpose: a message then renders from its preview
 * with a visible notice, which beats aborting the whole export. The bucket
 * allows cross-origin GET (see cors.json), so this normally succeeds.
 *
 * @param {object[]} ordered
 * @returns {Promise<object[]>}
 */
const hydrateRemoteBodies = async (ordered) => {
  const pending = ordered.filter(needsRemoteBody);
  if (pending.length === 0) return ordered;

  const fetched = new Map();
  await Promise.all(
    pending.map(async (msg) => {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller
        ? setTimeout(() => controller.abort(), REMOTE_BODY_TIMEOUT_MS)
        : null;
      try {
        const res = await fetch(msg.htmlStorageUrl, {
          signal: controller ? controller.signal : undefined,
        });
        if (!res.ok) return;
        const html = await res.text();
        if (html && html.trim()) fetched.set(msg, html);
      } catch (_) {
        /* offline, CORS or timeout — fall back to the preview + notice */
      } finally {
        if (timer) clearTimeout(timer);
      }
    })
  );

  if (fetched.size === 0) return ordered;
  return ordered.map((msg) =>
    fetched.has(msg) ? { ...msg, _exportHtml: fetched.get(msg) } : msg
  );
};

/**
 * Resolves once every image in the document has loaded or failed. Printing
 * before that would drop images from the PDF, and `readyState`/`load` are
 * unreliable for a document created via document.write.
 */
const waitForImages = (doc) => {
  const pending = Array.from((doc && doc.images) || []).filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();
  return Promise.all(
    pending.map(
      (img) =>
        new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        })
    )
  );
};

const STATUS_DOC = `<!DOCTYPE html>
<html lang="de"><head><meta charset="utf-8"><title>Chatverlauf wird vorbereitet…</title>
<style>body{margin:0;font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;background:#f5f6f8;color:#5b636e}
.tx-status{text-align:center;padding:80px 20px}</style></head>
<body><div class="tx-status">Chatverlauf wird vorbereitet…</div></body></html>`;

/**
 * Opens the transcript in a new tab and triggers the print dialog, which is
 * also the route to "Save as PDF". The tab stays open if the user cancels, so
 * the transcript (with its own print button) isn't lost.
 *
 * The tab is opened synchronously — before any awaiting — because popup
 * blockers only honour `window.open` while the click gesture is still active.
 * Bodies that live in Storage are loaded afterwards and written into the tab
 * that is already there.
 *
 * @param {object[]} messages
 * @param {object} [selectedEmail]
 * @returns {{ ok: boolean, reason?: 'empty'|'blocked' }}
 */
export const printThreadExport = (messages, selectedEmail) => {
  const ordered = orderChronologically(messages);
  if (ordered.length === 0) return { ok: false, reason: 'empty' };

  const win = window.open('', '_blank');
  if (!win) return { ok: false, reason: 'blocked' };

  win.document.open();
  win.document.write(STATUS_DOC);
  win.document.close();

  hydrateRemoteBodies(ordered)
    .catch(() => ordered)
    .then((hydrated) => {
      if (win.closed) return;

      win.document.open();
      win.document.write(buildThreadExportHtml(hydrated || ordered, selectedEmail));
      win.document.close();

      const doPrint = () => {
        if (win.closed) return;
        try {
          win.focus();
          win.print();
        } catch (_) {
          /* the tab is still usable via its own print button */
        }
      };

      // Print once the images have settled, but never wait on a hanging one.
      Promise.race([
        waitForImages(win.document),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ])
        .catch(() => {})
        .then(doPrint);
    });

  return { ok: true };
};

export default printThreadExport;
